import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  r => r.data,
  e => {
    if (e.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(e.response?.data?.error || e.message);
  }
);


/**
 * Variante che rilancia il CORPO della risposta di errore, non il solo
 * messaggio.
 *
 * Alcune risposte portano dati che servono a chi ha chiamato — una conferma da
 * chiedere, il valore massimo ammesso — e l'interceptor qui sopra li butta via
 * per far funzionare gli `alert(e)` sparsi nelle pagine. Dove quei dati
 * servono si usa questa istanza.
 */
const apiDettagliata = axios.create({ baseURL: '/api' });
apiDettagliata.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
apiDettagliata.interceptors.response.use(
  r => r.data,
  e => Promise.reject(e.response?.data ?? { error: e.message }),
);


/**
 * Scarica un file protetto da token.
 *
 * Un <a href> semplice non porta l'header Authorization e il server risponde
 * 401: il file va richiesto via axios e consegnato al browser come blob.
 */
/**
 * Apre un documento protetto in una scheda nuova.
 *
 * Un <a href> semplice non porta l'header Authorization: il server risponde
 * 401 e non si apre niente — è quello che succedeva agli allegati delle
 * offerte dei consulenti e ai giustificativi di spesa. Il file va quindi
 * chiesto via axios e consegnato al browser come blob.
 *
 * La scheda si apre SUBITO, dentro il clic dell'utente: aprirla dopo la
 * risposta la farebbe bloccare come finestra a comparsa. Se il blocco scatta
 * lo stesso, il file viene scaricato invece che perso.
 */
export async function apriProtetto(percorso, nomeFile) {
  const finestra = window.open('', '_blank');
  try {
    const r = await api.get(percorso, { responseType: 'blob' });
    if (r.type === 'application/json') {
      throw JSON.parse(await r.text()).error || 'File non disponibile';
    }
    const url = URL.createObjectURL(r);
    if (finestra && !finestra.closed) {
      finestra.location.href = url;
    } else {
      const a = document.createElement('a');
      a.href = url; a.download = nomeFile || 'documento';
      document.body.appendChild(a); a.click(); a.remove();
    }
    // Il blob si libera dopo, non subito: la scheda deve avere il tempo di leggerlo.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (finestra && !finestra.closed) finestra.close();
    throw e;
  }
}

/**
 * Il documento come blob, per mostrarlo in anteprima invece di scaricarlo.
 *
 * Un errore del server arriva comunque come blob — axios non lo distingue —
 * quindi va riletto come JSON, altrimenti al posto del documento comparirebbe
 * una pagina vuota senza spiegazioni.
 */
export async function blobProtetto(percorso, { metodo = 'get', dati } = {}) {
  const r = metodo === 'post'
    ? await api.post(percorso, dati, { responseType: 'blob' })
    : await api.get(percorso, { responseType: 'blob' });
  if (r.type === 'application/json') {
    throw JSON.parse(await r.text()).error || 'Errore di generazione';
  }
  return r;
}

export async function scaricaProtetto(percorso, nomeFile) {
  const r = await api.get(percorso, { responseType: 'blob' });
  if (r.type === 'application/json') {
    throw JSON.parse(await r.text()).error || 'File non disponibile';
  }
  const url = URL.createObjectURL(r);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeFile || 'documento';
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Generazione del documento di offerta. responseType blob: la risposta è un
// file, non JSON — e il download parte da qui, senza passare per un link.
export const documentoApi = {
  modelli: () => api.get('/documento-offerta/modelli'),
  scarica: async (offertaId, dati, formato, nomeFile) => {
    const r = await api.post(`/documento-offerta/${offertaId}?formato=${formato}`, dati, { responseType: 'blob' });
    // r è già response.data per l'interceptor. Se il server risponde con un
    // errore JSON, axios lo consegna comunque come blob: va riletto.
    if (r.type === 'application/json') {
      const testo = await r.text();
      throw JSON.parse(testo).error || 'Errore di generazione';
    }
    const url = URL.createObjectURL(r);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${nomeFile || 'Offerta'}.${formato === 'pdf' ? 'pdf' : 'docx'}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  },
};

// Consulenti che lavorano su una commessa. Assegnandoli qui si abilita
// anche il loro account alla rendicontazione dei SAL.
export const commesseConsulentiApi = {
  list: (commessaId) => api.get(`/commesse/${commessaId}/consulenti`),
  add: (commessaId, consulente_id) => api.post(`/commesse/${commessaId}/consulenti`, { consulente_id }),
  remove: (commessaId, consulenteId) => api.delete(`/commesse/${commessaId}/consulenti/${consulenteId}`),
};

// Allegati. tipo: 'offerta' | 'offerta-consulente' | 'commessa'
export const allegatiApi = {
  list: (tipo, id) => api.get(`/allegati/${tipo}/${id}`),
  carica: (tipo, id, file, categoria) => {
    const fd = new FormData();
    fd.append('file', file);
    if (categoria) fd.append('categoria', categoria);
    return api.post(`/allegati/${tipo}/${id}`, fd);
  },
  elimina: (allegatoId) => api.delete(`/allegati/file/${allegatoId}`),
  url: (allegatoId) => `/api/allegati/file/${allegatoId}`,
  scarica: (allegatoId, nome) => scaricaProtetto(`/allegati/file/${allegatoId}`, nome),
};

export const clientiApi = {
  list: (params) => api.get('/clienti', { params }),
  get: (id) => api.get(`/clienti/${id}`),
  create: (data) => api.post('/clienti', data),
  update: (id, data) => api.put(`/clienti/${id}`, data),
  delete: (id) => api.delete(`/clienti/${id}`),
};

export const offerteApi = {
  list: (params) => api.get('/offerte', { params }),
  get: (id) => api.get(`/offerte/${id}`),
  create: (data) => api.post('/offerte', data),
  update: (id, data) => api.put(`/offerte/${id}`, data),
  delete: (id) => api.delete(`/offerte/${id}`),
  converti: (id, data) => api.post(`/offerte/${id}/converti`, data),
  // Rinnovo: si fa SOLO quando lo si chiede, offerta per offerta. Niente
  // creazioni automatiche a calendario.
  rinnova: (id, data) => api.post(`/offerte/${id}/rinnova`, data),
  condizioni: (id) => api.get(`/offerte/${id}/condizioni`),
  nextNumero: (user_id, anno) => api.get('/offerte/next-numero', {
    params: { ...(user_id ? { user_id } : {}), ...(anno ? { anno } : {}) },
  }),
  // Offerte nate fuori dal gestionale: si legge il documento e si propongono
  // i campi, poi si crea l'offerta come tutte le altre.
  analizzaDocumento: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/offerte/analizza-documento', fd);
  },
  // Versioni: la fotografia di com'era l'offerta prima di ogni revisione
  versioni: (id) => api.get(`/offerte/${id}/versioni`),
  versione: (id, n) => api.get(`/offerte/${id}/versioni/${n}`),
  salvaVersione: (id, nota) => api.post(`/offerte/${id}/versioni`, { nota }),
};

// Listino delle attività a economia. Stesso percorso per offerte e commesse:
// cambia solo il contenitore ('offerta' | 'commessa').
export const listinoApi = {
  get: (tipo, id) => api.get(`/listino/${tipo}/${id}`),
  save: (tipo, id, voci) => api.put(`/listino/${tipo}/${id}`, { voci }),
};

export const commesseApi = {
  list: (params) => api.get('/commesse', { params }),
  get: (id) => api.get(`/commesse/${id}`),
  create: (data) => api.post('/commesse', data),
  update: (id, data) => api.put(`/commesse/${id}`, data),
  // params.offerta = 'inviata' riporta l'offerta di origine a "inviata"
  delete: (id, params) => api.delete(`/commesse/${id}`, { params }),
  // Cosa comporterebbe l'eliminazione: cosa la blocca, cosa verrebbe perso
  riepilogoEliminazione: (id) => api.get(`/commesse/${id}/eliminazione`),
  chiudi: (id, opzioni) => api.post(`/commesse/${id}/chiudi`, opzioni || {}),
  riepilogoChiusura: (id) => api.get(`/commesse/${id}/riepilogo-chiusura`),
  riapri: (id) => api.post(`/commesse/${id}/riapri`),
  chiudiMultiple: (ids) => api.post('/commesse/chiudi-multiple', { ids }),
};

export const fatturazionIApi = {
  list: (params) => api.get('/fatturazioni', { params }),
  get: (id) => api.get(`/fatturazioni/${id}`),
  create: (data) => api.post('/fatturazioni', data),
  update: (id, data) => api.put(`/fatturazioni/${id}`, data),
  delete: (id) => api.delete(`/fatturazioni/${id}`),
  // Riepilogo mensile con spunta "emessa davvero" (allineamento a Fatture in Cloud)
  riepilogo: (anno, mese) => api.get(`/fatturazioni/riepilogo/${anno}/${mese}`),
  // Spese anticipate e rendiconti in lavorazione: cosa c'è da fatturare che
  // non è ancora diventato una fattura
  daRiaddebitare: () => api.get('/fatturazioni/da-riaddebitare'),
  // Commesse ferme: residui di anni precedenti, fatture in coda scadute
  inRitardo: () => api.get('/fatturazioni/in-ritardo'),
  // Lavoro a corpo: quanto resta da mandare in fatturazione, e le tranche
  // (tutto o in parte, nel mese che si decide)
  daMandare: (commessaId) => api.get(`/fatturazioni/da-mandare/${commessaId}`),
  daMandareTutte: () => api.get('/fatturazioni/da-mandare'),
  pianifica: (data) => api.post('/fatturazioni/pianifica', data),
  spunta: (id, emessa, extra) => api.put(`/fatturazioni/${id}/emessa`, { emessa, ...(extra || {}) }),
  emettiPeriodica: (data) => api.post('/fatturazioni/emetti-periodica', data),
};

// Ordini del cliente: quanto è stato autorizzato e quanto ne resta
export const ordiniApi = {
  perCommessa: (commessaId) => api.get(`/ordini/commessa/${commessaId}`),
  create: (commessaId, data) => api.post(`/ordini/commessa/${commessaId}`, data),
  update: (id, data) => api.put(`/ordini/${id}`, data),
  delete: (id) => api.delete(`/ordini/${id}`),
  // Campi proposti leggendo il documento già allegato
  leggiAllegato: (allegatoId) => api.get(`/ordini/allegato/${allegatoId}/lettura`),
};

// Fatture ricevute dai consulenti, confrontate con i rendiconti approvati
export const fattureConsulentiApi = {
  list: () => api.get('/fatture-consulenti'),
  get: (id) => api.get(`/fatture-consulenti/${id}`),
  confronto: (params) => api.get('/fatture-consulenti/confronto', { params }),
  create: (dati, file) => api.post('/fatture-consulenti', conAllegato(dati, file)),
  update: (id, dati, file) => api.put(`/fatture-consulenti/${id}`, conAllegato(dati, file)),
  stato: (id, dati) => api.post(`/fatture-consulenti/${id}/stato`, dati),
  imputaCosti: (id) => api.post(`/fatture-consulenti/${id}/costi`),
  delete: (id) => api.delete(`/fatture-consulenti/${id}`),
  urlAllegato: (id) => `/api/fatture-consulenti/${id}/allegato`,
};

/** Campi + documento in un form-data: il PDF viaggia con i dati. */
function conAllegato(dati, file) {
  const fd = new FormData();
  Object.entries(dati || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') fd.append(k, v);
  });
  if (file) fd.append('allegato', file);
  return fd;
}

export const consulentiApi = {
  list: (params) => api.get('/consulenti', { params }),
  get: (id) => api.get(`/consulenti/${id}`),
  create: (data) => api.post('/consulenti', data),
  update: (id, data) => api.put(`/consulenti/${id}`, data),
  delete: (id) => api.delete(`/consulenti/${id}`),
};

export const offerteConsulentiApi = {
  // Gli ordini alle società li numeriamo noi: ODA + progressivo + anno.
  nextNumeroOda: (anno) => api.get('/offerte-consulenti/next-numero', { params: anno ? { anno } : {} }),
  list: (params) => api.get('/offerte-consulenti', { params }),
  get: (id) => api.get(`/offerte-consulenti/${id}`),
  create: (data) => api.post('/offerte-consulenti', data),
  update: (id, data) => api.put(`/offerte-consulenti/${id}`, data),
  delete: (id) => api.delete(`/offerte-consulenti/${id}`),
  offerteCollegabili: () => api.get('/offerte-consulenti/offerte-collegabili'),
  commesseCollegabili: () => api.get('/offerte-consulenti/commesse-collegabili'),
  approva: (id, data) => api.post(`/offerte-consulenti/${id}/approva`, data),
  rifiuta: (id, data) => api.post(`/offerte-consulenti/${id}/rifiuta`, data),
  // Controllo fatturazione: impegnato vs fatturato, con gli sforamenti
  controllo: (params) => api.get('/offerte-consulenti/controllo', { params }),

  // Lettura del documento allegato: propone i campi, non salva nulla
  analizzaDocumento: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/offerte-consulenti/analizza-documento', fd);
  },
  allegati: (id) => api.get(`/offerte-consulenti/${id}/allegati`),
  caricaAllegato: (id, file, categoria) => {
    const fd = new FormData();
    fd.append('file', file);
    if (categoria) fd.append('categoria', categoria);
    return api.post(`/offerte-consulenti/${id}/allegati`, fd);
  },
  // Lettera d'incarico da far firmare al consulente: scarica il file
  // dati: rimborsi ed esclusiva decisi per questo incarico (si salvano sull'offerta)
  incarico: async (id, formato, nomeFile, dati = {}) => {
    const r = await api.post(`/offerte-consulenti/${id}/incarico?formato=${formato}`, dati, { responseType: 'blob' });
    if (r.type === 'application/json') throw JSON.parse(await r.text()).error || 'Errore di generazione';
    const url = URL.createObjectURL(r);
    const a = document.createElement('a');
    a.href = url; a.download = `${nomeFile || 'Incarico'}.${formato === 'pdf' ? 'pdf' : 'docx'}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  },
  eliminaAllegato: (id, allegatoId) => api.delete(`/offerte-consulenti/${id}/allegati/${allegatoId}`),
  urlAllegato: (id, allegatoId) => `/api/offerte-consulenti/${id}/allegati/${allegatoId}`,
};

export const offerteUtentiApi = {
  list: (offerta_id) => api.get(`/offerte/${offerta_id}/utenti`),
  perUtente: (user_id) => api.get(`/offerte/utente/${user_id}`),
  add: (offerta_id, user_id) => api.post(`/offerte/${offerta_id}/utenti`, { user_id }),
  remove: (offerta_id, user_id) => api.delete(`/offerte/${offerta_id}/utenti/${user_id}`),
};

export const costiConsulentiApi = {
  list: (params) => api.get('/costi-consulenti', { params }),
  get: (id) => api.get(`/costi-consulenti/${id}`),
  create: (data) => api.post('/costi-consulenti', data),
  update: (id, data) => api.put(`/costi-consulenti/${id}`, data),
  delete: (id) => api.delete(`/costi-consulenti/${id}`),
};

export const salApi = {
  list: (params) => api.get('/sal', { params }),
  get: (id) => api.get(`/sal/${id}`),
  create: (data) => api.post('/sal', data),
  update: (id, data) => api.put(`/sal/${id}`, data),
  delete: (id) => api.delete(`/sal/${id}`),
  invia: (id) => api.post(`/sal/${id}/invia`),
  approva: (id, data) => api.post(`/sal/${id}/approva`, data),
  // Fatture in coda dello stesso cliente e mese, a cui accorpare il rendiconto
  accorpabili: (id) => api.get(`/sal/${id}/accorpabili`),
  rifiuta: (id, data) => api.post(`/sal/${id}/rifiuta`, data),
  commesseAccessibili: () => api.get('/sal/commesse-accessibili'),
  // Rendiconto da Excel: senza conferma restituisce solo l'anteprima
  importaRendiconto: (file, { mese, conferma, consulente_id } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (mese) fd.append('mese', mese);
    if (conferma) fd.append('conferma', 'true');
    if (consulente_id) fd.append('consulente_id', consulente_id);
    return api.post('/sal/importa', fd);
  },
  avanzamento: (commessaId) => api.get(`/sal/commesse/${commessaId}/avanzamento`),
  // righe di attività
  addAttivita: (id, data) => api.post(`/sal/${id}/attivita`, data),
  updAttivita: (id, rigaId, data) => api.put(`/sal/${id}/attivita/${rigaId}`, data),
  delAttivita: (id, rigaId) => api.delete(`/sal/${id}/attivita/${rigaId}`),
  vociCommessa: (commessaId) => api.get(`/sal/commesse/${commessaId}/voci`),
  // Il server ferma una quantità che le ore non giustificano e dice di quanto:
  // serve il corpo della risposta per poter chiedere conferma e ripetere.
  correggiQuantita: (id, rigaId, quantita, forza) =>
    apiDettagliata.put(`/sal/${id}/attivita/${rigaId}/quantita`,
                       forza ? { quantita, forza: true } : { quantita }),
  delete: (id) => api.delete(`/sal/${id}`),
  riapri: (id, note_admin) => api.post(`/sal/${id}/riapri`, { note_admin }),
  rettifica: (id, dati) => api.post(`/sal/${id}/rettifica`, dati),
  // Autorizzazione del cliente sulle attività aggiuntive
  extraStato: (id, dati) => api.post(`/sal/${id}/extra-stato`, dati),
  // Il consulente chiede l'autorizzazione per il lavoro fuori contratto
  extraRichiesta: (id, nota) => api.post(`/sal/${id}/extra-richiesta`, { nota }),
  // Attività scelte e fatturate insieme a un importo a corpo
  creaAccorpamento: (id, dati) => api.post(`/sal/${id}/accorpamenti`, dati),
  modificaAccorpamento: (id, accId, dati) => api.put(`/sal/${id}/accorpamenti/${accId}`, dati),
  sciogliAccorpamento: (id, accId) => api.delete(`/sal/${id}/accorpamenti/${accId}`),
  // Consuntivo per il cliente: da guardare in anteprima prima di scaricarlo
  anteprimaConsuntivo: (id) => blobProtetto(`/sal/${id}/consuntivo?formato=pdf`),
  scaricaConsuntivo: async (id, formato, nomeFile) => {
    const r = await blobProtetto(`/sal/${id}/consuntivo?formato=${formato}`);
    const url = URL.createObjectURL(r);
    const a = document.createElement('a');
    a.href = url; a.download = `${nomeFile || 'Consuntivo'}.${formato === 'pdf' ? 'pdf' : 'docx'}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  },
  // spese: viaggiano come form-data perché possono avere il giustificativo
  addSpesa: (id, campi, file) => {
    const fd = new FormData();
    Object.entries(campi).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') fd.append(k, v); });
    if (file) fd.append('allegato', file);
    return api.post(`/sal/${id}/spese`, fd);
  },
  // Come l'inserimento: form-data, perché la correzione può portare un
  // giustificativo che prima mancava.
  updSpesa: (id, rigaId, campi, file) => {
    const fd = new FormData();
    Object.entries(campi).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, v); });
    if (file) fd.append('allegato', file);
    return api.put(`/sal/${id}/spese/${rigaId}`, fd);
  },
  delSpesa: (id, rigaId) => api.delete(`/sal/${id}/spese/${rigaId}`),
  ribaltaSpesa: (id, rigaId, ribaltata) => api.put(`/sal/${id}/spese/${rigaId}/ribalta`, { ribaltata }),
  urlAllegato: (id, rigaId) => `/api/sal/${id}/spese/${rigaId}/allegato`,
};

export const authApi = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/password', data),
  users: () => api.get('/auth/users'),
  usersList: () => api.get('/auth/users-list'),
  password: (id) => api.get(`/auth/users/${id}/password`),
  createUser: (data) => api.post('/auth/users', data),
  // Firma depositata: finisce nelle offerte emesse dall'utente
  caricaFirma: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/auth/users/${id}/firma`, fd);
  },
  eliminaFirma: (id) => api.delete(`/auth/users/${id}/firma`),
  // L'immagine è protetta come il resto: un <img src> non può mandare il
  // token, quindi si scarica e si mostra da un URL temporaneo.
  firmaBlob: (id) => api.get(`/auth/users/${id}/firma`, { responseType: 'blob' }),
  updateUser: (id, data) => api.put(`/auth/users/${id}`, data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`),
};

export const commesseUtentiApi = {
  list: (commessa_id) => api.get(`/commesse/${commessa_id}/utenti`),
  perUtente: (user_id) => api.get(`/commesse/utente/${user_id}`),
  add: (commessa_id, user_id) => api.post(`/commesse/${commessa_id}/utenti`, { user_id }),
  remove: (commessa_id, user_id) => api.delete(`/commesse/${commessa_id}/utenti/${user_id}`),
};

export const dashboardApi = {
  get: (params) => api.get('/dashboard', { params }),
  offertePerUtente: (params) => api.get('/dashboard/offerte-per-utente', { params }),
};

export const importApi = {
  clienti: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/import/clienti', fd); },
  consulenti: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/import/consulenti', fd); },
};

export default api;

export const templateApi = {
  list: (params) => api.get('/offerta-template', { params }),
  get: (id) => api.get(`/offerta-template/${id}`),
  create: (data) => api.post('/offerta-template', data),
  update: (id, data) => api.put(`/offerta-template/${id}`, data),
  delete: (id) => api.delete(`/offerta-template/${id}`),
  // Il .docx viaggia come multipart: niente Content-Type a mano, lo mette il browser
  // con il boundary corretto.
  caricaFile: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/offerta-template/${id}/file`, fd);
  },
  scaricaFile: (id, nome) => scaricaProtetto(`/offerta-template/${id}/file`, nome),
  eliminaFile: (id) => api.delete(`/offerta-template/${id}/file`),

  tipologie: () => api.get('/offerta-template/tipologie'),
  creaTipologia: (data) => api.post('/offerta-template/tipologie', data),
  aggiornaTipologia: (id, data) => api.put(`/offerta-template/tipologie/${id}`, data),
  eliminaTipologia: (id) => api.delete(`/offerta-template/tipologie/${id}`),
};

export const segnalazioniApi = {
  crea: (data) => api.post('/segnalazioni', data),
  mie: () => api.get('/segnalazioni/mie'),
  segnaLetta: (id) => api.post(`/segnalazioni/${id}/letta`),
  list: (params) => api.get('/segnalazioni', { params }),
  aggiorna: (id, data) => api.put(`/segnalazioni/${id}`, data),
  elimina: (id) => api.delete(`/segnalazioni/${id}`),
};

// Indice ISTAT per l'adeguamento dei rinnovi. Letto dall'ISTAT quando risponde,
// altrimenti dall'ultimo valore conservato o scritto a mano.
export const istatApi = {
  corrente: (aggiorna) => api.get('/istat', { params: aggiorna ? { aggiorna: 1 } : {} }),
  salva: (data) => api.post('/istat', data),
};

// Scadenziario formativo dei clienti. Lo staff scrive, l'utente del cliente
// legge: il controllo vero sta sul server, qui ci sono solo le chiamate.
export const formazioneApi = {
  riepilogo: () => api.get('/formazione/riepilogo'),
  cliente: (clienteId) => api.get(`/formazione/${clienteId}`),
  scadenziario: (clienteId) => api.get(`/formazione/${clienteId}/scadenziario`),
  salvaCorsi: (clienteId, corsi) => api.put(`/formazione/${clienteId}/corsi`, { corsi }),

  creaPersona: (clienteId, dati) => api.post(`/formazione/${clienteId}/personale`, dati),
  aggiornaPersona: (id, dati) => api.put(`/formazione/personale/${id}`, dati),
  eliminaPersona: (id) => api.delete(`/formazione/personale/${id}`),

  creaAttestato: (personaId, dati, file) => {
    const fd = new FormData();
    Object.entries(dati).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
    if (file) fd.append('file', file);
    return api.post(`/formazione/personale/${personaId}/attestati`, fd);
  },
  eliminaAttestato: (id) => api.delete(`/formazione/attestati/${id}`),
  // Il certificato è protetto come tutto il resto: si scarica con il token e
  // si apre da un indirizzo temporaneo.
  apriCertificato: async (id) => {
    const b = await api.get(`/formazione/attestati/${id}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(b);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },

  analizza: (clienteId, file, foglio) => {
    const fd = new FormData();
    fd.append('file', file);
    if (foglio) fd.append('foglio', foglio);
    return api.post(`/formazione/${clienteId}/analizza`, fd);
  },
  importa: (clienteId, dati) => api.post(`/formazione/${clienteId}/importa`, dati),

  // Catalogo dei corsi che eroghiamo noi, e abbinamento con quelli del cliente
  catalogo: () => api.get('/formazione/catalogo/corsi'),
  salvaCatalogo: (corsi) => api.put('/formazione/catalogo/corsi', { corsi }),
  sessioni: (tutte) => api.get('/formazione/catalogo/sessioni', { params: tutte ? { tutte: 1 } : {} }),
  salvaSessioni: (sessioni, tutte) => api.put('/formazione/catalogo/sessioni',
    { sessioni, ambito: tutte ? 'tutte' : 'future' }),
  abbinaCorso: (corsoId, catalogo_id) =>
    api.put(`/formazione/corsi/${corsoId}/catalogo`, { catalogo_id }),

  // La comunicazione al cliente: prima si guarda, poi si scarica
  anteprimaComunicazione: (clienteId, giorni, conScaduti) =>
    api.get(`/formazione/${clienteId}/comunicazione`,
            { params: { giorni, scaduti: conScaduti ? 1 : 0 } }),
  scaricaComunicazione: (clienteId, { giorni, conScaduti, referente }, formato, nomeCliente) => {
    const q = new URLSearchParams({ giorni, scaduti: conScaduti ? 1 : 0, formato });
    if (referente) q.set('referente', referente);
    return scaricaProtetto(`/formazione/${clienteId}/comunicazione?${q}`,
      `Scadenze_formative_${String(nomeCliente || 'cliente').replace(/[^\w-]+/g, '_')}.${formato}`);
  },
};
